import { PAGE_TYPE } from '@/app/common/pageType.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { isEmptyObject } from '@/app/components/search/Utilities.ts';
import { createSearchParamsFromUrl } from '@/app/common/createSearchParamsFromUrl.ts';
import { isDestinationsPageFromSearchParams } from '@/app/components/search/utils/searchTypesUtils.ts';
import { SEARCH_TYPE, type SearchInfoData } from '@/app/search/types.ts';
import { removeParameters, updateUrl } from '@/app/common/urlParametersUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { createResultsParamsToDrop } from '@/app/common/createResultsParamsToDrop.ts';
import { hasSearchPlacesChanged, shouldRefreshPageWrapperData } from '@/app/common/shouldRefreshPageWrapperData.ts';
import { BASE_PATH } from '@/app/components/search/utils/path.ts';
import { createQueryString } from '@/utils/queryUtils.ts';
import { addBrowserHistory, replaceBrowserHistory } from '@/app/common/browserHistory.ts';
import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { fetchPageWrapperData } from '@/app/common/refreshPageWrapperData.ts';
import { type Dispatch, type SetStateAction, startTransition } from 'react';
import type { SearchDefaultData } from '@/app/search/createSearchDefaultData.ts';
import { toSearchDefaultDataLocalization } from '@/app/search/searchDefaultDataLocalization.ts';
import type { SearchDefaultDataPayload } from '@/app/api/search/searchDefaultData/route.ts';
import type { DefaultValuesCSW } from '@/app/common/CSW/services/types.ts';
import { createLiveHandler } from '@/app/common/liveHandler.ts';
import { isLatestCSWRefresh, startCSWRefresh } from '@/app/common/cswRefresh.ts';
import type { SearchPageData } from '@/app/search/createSearchPageData.ts';
import type { SearchPageDataPayload } from '@/app/api/search/searchPageData/route.ts';
import handleMapLoad from '@/app/components/search/common/reloadData/handleMapLoad.ts';
import { ReloadType } from '@/app/components/search/utils';
import { FORCE_CONFIG_URL_PARAM_PREFIX } from '@lmn/lmn-commonlogic/dist/config/configuration';
import { SOURCE } from '@/app/components/search/app';
import { createPlatformSearchId } from '@/utils/searchIdUtils.ts';
import { createSubSourceForNewSearch } from '@/app/common/starTrack/events/searchRequested.ts';
import type {
  CreatePageWrapperClientSearchProps,
  FireSearchEventsAfterProps,
  FireSearchEventsBeforeProps,
  HandleDynamicFiltersResponseChangedProps,
  HandleNewSearch,
  HandleSearchChanged,
  HandleSearchChangedBaseProps,
  HandleSearchChangedProps,
  HandleSearchProductsDataChanged,
} from '@/app/common/PageWrapperClientTypes.tsx';
import { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import type { HandleWaitingFinishedParams } from '@/app/components/search/common/waitingCall.ts';
import { waitingCall } from '@/app/components/search/common/waitingCall.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import {
  fireSearchDestinationsEventsAfter,
  fireSearchDestinationsEventsBefore,
  fireSearchProductsEventsAfter,
  fireSearchProductsEventsBefore,
} from '@/app/common/PageWrapperClientEventsUtils.tsx';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { isFalsyOrEmpty } from '@/app/common/utils/utility.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { EXPERIMENT_NAMES } from '@/app/common/ABTesting/ABTestingVariants.ts';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import {
  appendSearchLoadingStage,
  setSearchLoadingStage,
  watchSearchDataReady,
} from '@/app/components/search/logging/searchLoadingWatchdog.ts';
import type { FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import { buildFilterGroups } from '@/app/common/filters/groups/buildFilterGroups.ts';
import { dynamicFiltersSearchKey } from '@/app/common/filters/core/dynamicFiltersSearchKey.ts';

let latestCallId = 0;

// See liveHandler.ts for why the widget must not keep the handlers of its first render.
const newSearchLive = createLiveHandler<HandleNewSearch>('handleNewSearch');
export const publishHandleNewSearch = newSearchLive.publish;
export const unpublishHandleNewSearch = newSearchLive.unpublish;
export const handleNewSearchLive = newSearchLive.live;

const defaultValuesCSWLive = createLiveHandler<Dispatch<SetStateAction<DefaultValuesCSW>>>('setDefaultValuesCSW');
export const publishSetDefaultValuesCSW = defaultValuesCSWLive.publish;
export const unpublishSetDefaultValuesCSW = defaultValuesCSWLive.unpublish;
export const setDefaultValuesCSWLive = defaultValuesCSWLive.live;

const scrollTo = (scrollY: number) => {
  setTimeout(() => {
    window.scrollTo(0, scrollY);
  }, 10);
};

const createOldParamsToKeep = (paramNames: string[], oldSearchParams: SearchParams) =>
  paramNames.reduce(
    (result, paramName: string) =>
      oldSearchParams[paramName] ? { ...result, [paramName]: oldSearchParams[paramName] } : result,
    {}
  );

const paramNameContainsAbTestName = (paramName: string): boolean =>
  Object.values(EXPERIMENT_NAMES).some((experimentName: string) => paramName.indexOf(experimentName) > -1);

type EnhanceNewSearchParams = {
  oldSearchParams: SearchParams;
  newSearchParams: SearchParams;
  newSearchType: SEARCH_TYPE;
  newPageType: PAGE_TYPE;
  userId: string;
};
const enhanceNewSearchParams = ({
  oldSearchParams,
  newSearchParams,
  newSearchType,
  newPageType,
  userId,
}: EnhanceNewSearchParams): SearchParams => {
  const configParams = Object.keys(oldSearchParams)
    .filter(
      (paramName) => paramName.startsWith(FORCE_CONFIG_URL_PARAM_PREFIX) || paramNameContainsAbTestName(paramName)
    )
    .reduce((acc: Record<string, string | undefined>, key: string) => {
      acc[key] = oldSearchParams[key];
      return acc;
    }, {});

  return {
    ...createOldParamsToKeep(
      [
        URLParameters.BUSINESS_PROFILE_ID,
        URLParameters.PINK_PEOPLE,
        URLParameters.DEBUG,
        URLParameters.HOST_NAME,
        URLParameters.ALLOW_DESKTOP,
        URLParameters.SEED,
        URLParameters.VIEW_MODE_DESKTOP,
        URLParameters.APP,
        URLParameters.APPVIEW,
      ],
      oldSearchParams
    ),
    [URLParameters.SOURCE]:
      newSearchType === SEARCH_TYPE.DESTINATIONS ? SOURCE.WIDGET_TSX_INDEX : SOURCE.WIDGET_TSX_MAP,
    [URLParameters.SEARCH_ID]: createPlatformSearchId(userId),
    [URLParameters.BF_SUB_SOURCE]: createSubSourceForNewSearch({ oldSearchParams, newPageType }),
    ...configParams,
    ...newSearchParams,
    // Apply the same origin default as the middleware (urlParamsValidations.ts) for DP searches.
    // The middleware only runs on full page loads, not on client-side navigations triggered by the
    // search widget. Without this fallback, SEARCH_REQUESTED would fire with departure=null whenever
    // the widget omits origin in a DP or TH search.
    ...(!newSearchParams[URLParameters.ORIGIN] &&
    (!newSearchParams[URLParameters.SEARCH_MODE] || newSearchParams[URLParameters.SEARCH_MODE] === SearchModes.DP)
      ? { [URLParameters.ORIGIN]: 'LON' }
      : {}),
  };
};

export const createPageWrapperClientSearch = ({
  commonData,
  pageWrapperData,
  searchPageData,
  searchDefaultData,
  localization,
  scrollPosSearch,
  mapRef,
  isMobile,
  translationDictionary,
  starTrack,
  createUpdatedDefaultValues,
  scrollToTop,
  setDefaultValuesCSW,
  setPageType,
  setPageWrapperData,
  setSearchPageData,
  setHotelId,
  getListView,
}: CreatePageWrapperClientSearchProps) => {
  const fireSearchEventsBefore = ({
    searchParams,
    searchInfoData,
    searchInfoDetailData,
    localization: localizationToUse,
    commonData: commonDataToUse,
    curiosityParams: curiosityParamsToUse,
  }: FireSearchEventsBeforeProps) => {
    if (searchInfoData.searchType === SEARCH_TYPE.PRODUCTS) {
      fireSearchProductsEventsBefore({
        searchParams,
        localization: localizationToUse,
        commonData: commonDataToUse,
        searchInfoData,
        searchInfoDetailData,
        starTrack,
      });
    }

    if (searchInfoData.searchType === SEARCH_TYPE.DESTINATIONS) {
      fireSearchDestinationsEventsBefore({
        searchParams,
        localization: localizationToUse,
        commonData: commonDataToUse,
        curiosityParams: curiosityParamsToUse,
        searchInfoData,
        searchInfoDetailData,
        starTrack,
      });
    }
  };

  const fireSearchEventsAfter = ({
    isNewSearch,
    searchParams,
    productsData,
    searchInfoData,
    searchInfoDetailData,
    commonData: commonDataToUse,
    localization: localizationToUse,
    curiosityParams: curiosityParamsToUse,
    defaultDestItems,
  }: FireSearchEventsAfterProps) => {
    if (searchInfoData.searchType === SEARCH_TYPE.PRODUCTS) {
      fireSearchProductsEventsAfter({
        listView: getListView(),
        isNewSearch,
        searchParams,
        productsData,
        searchInfoData,
        searchInfoDetailData,
        defaultDestItems,
        commonData: commonDataToUse,
        localization: localizationToUse,
        curiosityParams: curiosityParamsToUse,
        starTrack,
      });
    }

    if (searchInfoData.searchType === SEARCH_TYPE.DESTINATIONS) {
      fireSearchDestinationsEventsAfter({
        searchParams,
        productsData,
        searchInfoData,
        searchInfoDetailData,
        defaultDestItems,
        commonData: commonDataToUse,
        localization: localizationToUse,
        curiosityParams: curiosityParamsToUse,
        starTrack,
      });
    }
  };

  const handleSearchChangedBase = async ({
    newPageType,
    newSearchParams,
    isNewSearch = false,
    refreshCSW = true,
    refreshSearch,
    scrollPos,
    shouldAddBrowserHistory,
    highlightedHotel,
    isChangeListView,
    isBrowserBackNavigation = false,
    isAlternativeDates = false,
    keepPage = false,
  }: HandleSearchChangedBaseProps) => {
    latestCallId += 1;
    const myCallId = latestCallId;
    // Taken before the page data call below, so a widget refresh still running is dropped as soon as
    // this one is requested, not only once this one starts. Sort, filter, pagination and map calls do
    // not refresh the widget, and must not cancel a refresh that is still running (see cswRefresh.ts).
    const myCSWRefreshId = refreshCSW ? startCSWRefresh() : undefined;
    const { userUid } = commonData;
    const { searchInfoData, searchInfoDetailData } = pageWrapperData;

    // The same question is asked three times below - whether this navigation ends in a fresh
    // request for the results - so it is decided once here.
    const willRedoSearch = refreshSearch || isEmptyObject(searchPageData?.productsData);

    if (willRedoSearch) {
      scrollToTop();
      setSearchLoadingStage(isNewSearch ? 'newSearch' : 'searchChanged');
      handleIsLoadingChanged(true);
    } else {
      scrollTo(scrollPos !== undefined ? scrollPos : scrollPosSearch.current);
    }

    const oldSearchParams = createSearchParamsFromUrl();
    const isDestinations = isDestinationsPageFromSearchParams({
      searchParams: newSearchParams || oldSearchParams,
      geodbLiveCountrySearch: localization.configValuesApp.geodbLiveCountrySearch,
    });
    const newSearchType = isDestinations ? SEARCH_TYPE.DESTINATIONS : SEARCH_TYPE.PRODUCTS;
    const newPageTypeToUse =
      newPageType === PAGE_TYPE.SEARCH_DESTINATIONS || newSearchType === SEARCH_TYPE.DESTINATIONS
        ? PAGE_TYPE.SEARCH_DESTINATIONS
        : PAGE_TYPE.SEARCH;
    const newSearchParamsToUse = newSearchParams
      ? enhanceNewSearchParams({
          oldSearchParams,
          newSearchParams,
          newSearchType,
          newPageType: newPageTypeToUse,
          userId: userUid || '',
        })
      : oldSearchParams;
    const searchParamsNextPage = removeParameters(
      {
        ...newSearchParamsToUse,
        [URLParameters.PAGE_TYPE]: newPageTypeToUse,
        ...(highlightedHotel ? { [URLParameters.HIGHLIGHT_HOTEL_ID]: `${highlightedHotel}` } : {}),
      },
      createResultsParamsToDrop({
        highlightedHotel,
        isNewSearch,
        isChangeListView,
        isAlternativeDates,
        isBrowserBackNavigation,
        keepPage,
      })
    );

    const urlNextPage = new URL(`${BASE_PATH}?${createQueryString(searchParamsNextPage)}`, window.location.href);
    if (shouldAddBrowserHistory) {
      addBrowserHistory(urlNextPage);
    } else {
      replaceBrowserHistory(urlNextPage);
    }

    setPageType(newPageTypeToUse);

    let pageWrapperDataToUse = pageWrapperData;
    // True when a browser navigation could not refresh the page data. The data the page holds was
    // built for the previous search, so its searchId and search type must not be written over the
    // restored URL or into the events of the search done for it.
    let isPageDataStale = false;
    const isSearchChanged = hasSearchPlacesChanged(pageWrapperData.searchParams, searchParamsNextPage);
    if (shouldRefreshPageWrapperData({ isNewSearch, isBrowserBackNavigation, willRedoSearch, isSearchChanged })) {
      const pageWrapperDataUpdated = await fetchPageWrapperData({
        searchParams: searchParamsNextPage,
        traceParent: searchInfoData.searchTraceParent,
        commonData,
      });

      if (!pageWrapperDataUpdated && !isBrowserBackNavigation) {
        // PAGE_WRAPPER_DATA failed — the new search cannot proceed with stale data.
        // Revert the URL and page type to what they were before the attempt, and let
        // the user retry rather than firing events with a mismatched searchId/destination.
        const oldUrl = new URL(`${BASE_PATH}?${createQueryString(oldSearchParams)}`, window.location.href);
        replaceBrowserHistory(oldUrl);
        setPageType(oldSearchParams[URLParameters.PAGE_TYPE] as PAGE_TYPE);
        handleIsLoadingChanged(false);
        return;
      }

      if (pageWrapperDataUpdated) {
        pageWrapperDataToUse = pageWrapperDataUpdated;
        setPageWrapperData(pageWrapperDataUpdated);
      } else {
        // A browser navigation: the browser has already moved, so oldSearchParams is the URL of the
        // page being restored and there is nothing to revert to. Go on with the page data the page
        // holds, so the widget refresh and the search below still run for the restored URL. The
        // widget places come from this page data, so they can still describe the previous search.
        clientAppLog({
          level: 'WARN',
          codeSourceName: 'pageWrapperClientSearch',
          logMessage: `Page data not refreshed on a browser navigation, the page keeps the current data. searchParams:${JSON.stringify(searchParamsNextPage)}`,
          url: window?.location?.href,
        });
        isPageDataStale = true;
        pageWrapperDataToUse = {
          ...pageWrapperDataToUse,
          commonData: { ...pageWrapperDataToUse.commonData, pageStartTime: Date.now() },
        };
      }
    } else {
      pageWrapperDataToUse = {
        ...pageWrapperDataToUse,
        commonData: { ...pageWrapperDataToUse.commonData, pageStartTime: Date.now() },
      };
    }

    // Update the searchId in the url in case it changed coming from the BE in the new pageWrapperData object. This happens in
    // the local env as Casper changes the userId every time (In QA and Production the userId should always be the same).
    // Stale page data holds the searchId of the previous search, and the restored URL already has the right one.
    const updatedSearchId = pageWrapperDataToUse.searchInfoData.searchId;
    const searchIdsDifferent = updatedSearchId !== oldSearchParams[URLParameters.SEARCH_ID];
    const shouldUpdateSearchId = !isPageDataStale && !!updatedSearchId && searchIdsDifferent;
    if (shouldUpdateSearchId) {
      clientAppLog({
        level: 'INFO',
        codeSourceName: 'pageWrapperClientSearch',
        logMessage: `REF999 searchId updated. updatedSearchId:${updatedSearchId}, oldSearchId:${oldSearchParams[URLParameters.SEARCH_ID]}`,
        url: window?.location?.href,
      });
      updateUrl({ queryParamsToSet: [{ [URLParameters.SEARCH_ID]: updatedSearchId }] });
    }

    if (myCSWRefreshId !== undefined) {
      startTransition(async () => {
        const updatedSearchDefaultData = await clientAPICall<SearchDefaultDataPayload, SearchDefaultData>({
          apiName: API_NAME.SEARCH_DEFAULT_DATA,
          payload: {
            pageType: newPageTypeToUse,
            searchParams: searchParamsNextPage,
            localization: toSearchDefaultDataLocalization(pageWrapperDataToUse.localization),
          },
          traceParent: searchInfoData.searchTraceParent,
          commonData: pageWrapperDataToUse.commonData,
        });
        // A newer widget refresh started while this call was running: it owns the widget now, so a
        // late answer must not overwrite it with the values of the older search.
        if (!isLatestCSWRefresh(myCSWRefreshId)) return;
        // The call failed, or an edge layer answered with a JSON error body instead of the defaults:
        // keep the CSW values it has now instead of replacing them with nothing.
        if (!updatedSearchDefaultData?.defaultValues) {
          clientAppLog({
            level: 'WARN',
            codeSourceName: 'pageWrapperClientSearch',
            logMessage: `Search widget defaults not refreshed, the searchDefaultData answer had no defaultValues. searchParams:${JSON.stringify(searchParamsNextPage)}`,
            url: window?.location?.href,
          });
          return;
        }
        // The page type and the search params of the results page, not of the page data in state:
        // on a browser forward from the hotel details the state can still hold the details page
        // data, and its departure intervals would put the widget on the hotel's exact dates while
        // the results are a flexible search.
        const updatedDefaultValues = createUpdatedDefaultValues(
          updatedSearchDefaultData.defaultValues,
          pageWrapperDataToUse.searchInfoDetailData,
          newPageTypeToUse,
          searchParamsNextPage
        );
        setDefaultValuesCSW(updatedDefaultValues);
      });
    }

    if (willRedoSearch) {
      let searchPageDataResponse = {} as SearchPageData;
      // Stale page data describes the previous search: take the searchId and the search type from
      // the restored URL, so the results and their events belong to the search they are made for.
      const searchInfoDataToUse: SearchInfoData = isPageDataStale
        ? {
            ...pageWrapperDataToUse.searchInfoData,
            searchId: searchParamsNextPage[URLParameters.SEARCH_ID] || pageWrapperDataToUse.searchInfoData.searchId,
            searchType: newSearchType,
          }
        : pageWrapperDataToUse.searchInfoData;
      const searchInfoDetailDataToUse = pageWrapperDataToUse?.searchInfoDetailData || searchInfoDetailData;
      try {
        if (isNewSearch && myCallId === latestCallId) {
          fireSearchEventsBefore({
            searchParams: searchParamsNextPage,
            searchInfoData: searchInfoDataToUse,
            searchInfoDetailData: searchInfoDetailDataToUse,
            localization: pageWrapperDataToUse.localization,
            commonData: pageWrapperDataToUse.commonData,
            curiosityParams: pageWrapperDataToUse.curiosityParams,
          });
        }

        const searchPageDataPayload: SearchPageDataPayload = {
          searchParams: searchParamsNextPage,
        };
        searchPageDataResponse = await clientAPICall<SearchPageDataPayload, SearchPageData>({
          apiName: API_NAME.SEARCH_PAGE_DATA,
          payload: searchPageDataPayload,
          traceParent: searchInfoDataToUse.searchTraceParent,
          commonData,
        });

        if (!isFalsyOrEmpty(searchPageDataResponse)) {
          // The server builds the groups without the dynamic filters, which reads as every chip
          // being available, so keep the ones on screen while they still describe this search. They
          // are derived from the response, so the three move together or not at all.
          setSearchPageData((prevSearchPageData) =>
            prevSearchPageData.dynamicFilters?.searchKey === dynamicFiltersSearchKey(searchParamsNextPage)
              ? {
                  ...searchPageDataResponse,
                  dynamicFilters: prevSearchPageData.dynamicFilters,
                  filtersGroups: prevSearchPageData.filtersGroups,
                }
              : searchPageDataResponse
          );
        }

        if (searchPageDataResponse?.productsData?.apiStatus === APIStatusEnum.WAIT) {
          appendSearchLoadingStage('polling');
          const coordinates = { lat: searchPageData.lat, lon: searchPageData.lon };
          handleMapLoad({
            searchInfoDetailData,
            reloadType: ReloadType.SEARCH,
            mapRef,
            searchParams: searchParamsNextPage,
            dealCards: searchPageDataResponse?.productsData?.dealCards,
            coordinates,
            isMobile,
          });

          const searchParamsInitial = getSearchParams() as SearchParams;
          waitingCall({
            searchParamsInitial,
            commonData: pageWrapperDataToUse.commonData,
            localization: pageWrapperDataToUse.localization,
            handleWaitingFinished: handleWaitingFinishedIfCurrent(myCallId),
            setAPIStatus: handleSearchAPIStatusChanged,
            curiosityParams: pageWrapperDataToUse.curiosityParams,
            isNewSearch,
          });
        } else {
          handleWaitingFinishedIfCurrent(myCallId)({
            isNewSearch,
            searchParams: searchParamsNextPage,
            productsData: searchPageDataResponse?.productsData,
            searchInfoData: searchInfoDataToUse,
            searchInfoDetailData: searchInfoDetailDataToUse,
            commonDataToUse: pageWrapperDataToUse.commonData,
            localizationToUse: pageWrapperDataToUse.localization,
            curiosityParamsToUse: pageWrapperDataToUse.curiosityParams,
          });
        }
      } catch (e) {
        // Nothing on this path clears either loading flag, so the watchdog reports it as stuck.
        appendSearchLoadingStage('postResponseError');
        console.log('Error creating search page data', e);
      }
    }
  };

  const handleSearchChanged: HandleSearchChanged = async ({
    newSearchParams,
    shouldAddBrowserHistory = true,
    isNewSearch = false,
    isChangeListView = false,
    isAlternativeDates = false,
    keepPage = false,
  }: HandleSearchChangedProps) => {
    await handleSearchChangedBase({
      newSearchParams,
      refreshSearch: true,
      refreshCSW: !!isAlternativeDates,
      shouldAddBrowserHistory,
      scrollPos: 0,
      isNewSearch,
      isChangeListView,
      isAlternativeDates,
      keepPage,
    });
  };

  const handleNewSearch: HandleNewSearch = async (newSearchParams) => {
    // eslint-disable-next-line no-param-reassign
    scrollPosSearch.current = 0;
    setHotelId('');

    await handleSearchChangedBase({
      newSearchParams,
      isNewSearch: true,
      refreshSearch: true,
      shouldAddBrowserHistory: true,
      scrollPos: 0,
    });
  };

  const handleWaitingFinished = async ({
    searchParams,
    productsData,
    searchInfoData,
    searchInfoDetailData,
    isNewSearch,
    commonDataToUse,
    localizationToUse,
    curiosityParamsToUse,
  }: HandleWaitingFinishedParams) => {
    // Every client-side results-page search converges here, so this is where the data stops being the
    // reason the placeholders are up.
    watchSearchDataReady();

    if (productsData) {
      const { vcSearchId } = productsData;
      const searchParamsFresh = getSearchParams();
      const vcSearchIdShouldUpdate =
        vcSearchId !== '' ||
        (searchParamsFresh[URLParameters.VC_SEARCH_ID] && searchParamsFresh[URLParameters.VC_SEARCH_ID] !== vcSearchId);
      if (vcSearchIdShouldUpdate) {
        searchParamsFresh[URLParameters.VC_SEARCH_ID] = vcSearchId || '';
        window.history.replaceState(null, '', `?${createQueryString(searchParamsFresh)}`);
      }

      handleSearchProductsDataChanged(productsData);

      const coordinates = { lat: searchPageData.lat, lon: searchPageData.lon };
      handleMapLoad({
        searchInfoDetailData,
        reloadType: ReloadType.SEARCH,
        mapRef,
        searchParams,
        dealCards: productsData.dealCards,
        coordinates,
        isMobile,
      });
    }

    fireSearchEventsAfter({
      isNewSearch,
      searchParams,
      productsData: productsData || ({} as ProductsData),
      searchInfoData,
      searchInfoDetailData,
      defaultDestItems: searchDefaultData.defaultDestItems,
      commonData: commonDataToUse,
      localization: localizationToUse,
      curiosityParams: curiosityParamsToUse,
    });

    handleIsLoadingChanged(false);
  };

  // Superseded searches must stop before handleWaitingFinished, not part-way through it: everything in
  // there (watchdog, URL, products, loading flag) belongs to the search that is still current.
  // Defaults to the id current at arming time, which is what the initial-load poll needs: it has no
  // search of its own, so it is superseded by the first client search that starts after it.
  const handleWaitingFinishedIfCurrent =
    (myCallId: number = latestCallId) =>
    (params: HandleWaitingFinishedParams) => {
      if (myCallId !== latestCallId) return;
      handleWaitingFinished(params);
    };

  const handlePagination = async (newSearchParams: SearchParams) => {
    await handleSearchChanged({ newSearchParams, keepPage: true });
  };

  const handleFiltersGroupsChanged = (newFiltersGroups: FiltersGroups) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        filtersGroups: newFiltersGroups,
      };

      return newSearchPageData;
    });
  };

  const handleDynamicFiltersResponseChanged = ({
    newDynamicFiltersResponse,
    searchParams,
    searchInfoData,
    productsData,
  }: HandleDynamicFiltersResponseChangedProps) => {
    const updatedFiltersGroups = buildFilterGroups({
      searchParams,
      searchInfoData,
      loggedIn: commonData.authResponse.loggedIn,
      commonData,
      localization,
      productsData,
      dynamicFiltersResponse: newDynamicFiltersResponse.filters,
      translationDictionary,
    });

    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        // Stored even when the call failed: the bar then shows what it can rather than waiting for
        // a response that is not coming.
        dynamicFilters: { searchKey: dynamicFiltersSearchKey(searchParams), response: newDynamicFiltersResponse },
        filtersGroups: updatedFiltersGroups,
      };

      return newSearchPageData;
    });
  };

  const handleSearchProductsDataChanged: HandleSearchProductsDataChanged = (newProductsData: ProductsData) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        productsData: newProductsData,
        isLoading: newProductsData.apiStatus === APIStatusEnum.WAIT,
      };

      return newSearchPageData;
    });
  };

  const handleSearchAPIStatusChanged = (newApiStatus: APIStatusEnum) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        productsData: {
          ...prevSearchPageData.productsData,
          apiStatus: newApiStatus,
        },
        isLoading: newApiStatus === APIStatusEnum.WAIT,
      };

      return newSearchPageData;
    });
  };

  const handleIsLoadingChanged = (newIsLoading: boolean) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        isLoading: newIsLoading,
      };

      return newSearchPageData;
    });
  };

  return {
    fireSearchEventsBefore,
    fireSearchEventsAfter,
    handleSearchChangedBase,
    handleSearchChanged,
    handleNewSearch,
    handleWaitingFinishedIfCurrent,
    handlePagination,
    handleFiltersGroupsChanged,
    handleDynamicFiltersResponseChanged,
    handleSearchProductsDataChanged,
    handleSearchAPIStatusChanged,
    handleIsLoadingChanged,
  };
};
